import {
  makeObservable,
  observable,
  action,
  IReactionDisposer,
  computed,
  IObservableArray,
} from 'mobx'
import TemplateItem from './TemplateItem'
import { v4 as uuidv4 } from 'uuid'
import Store from '../models/Store'
import TemplateSection from './TemplateSection'
import { ProviderTemplateSection } from '../providers/types'

export default class Template {
  id: string
  name: string = ''
  deadline: number = 0
  items: IObservableArray<TemplateItem> = observable.array([])
  sections: TemplateSection[] = []
  timestamp: number = 0
  saving: boolean = false
  store: Store
  deleted: boolean = false
  disposeSaveHandler?: IReactionDisposer
  lastError: string = ''

  constructor(store: Store, name: string) {
    makeObservable(this, {
      id: false,
      name: observable,
      items: observable,
      sections: observable,
      timestamp: observable,
      deleted: observable,
      deadline: observable,
      saving: observable,
      lastError: observable,
      hasError: computed,
      sortedItems: computed,
      sortedItemsWithoutSection: computed,
      itemLookup: computed,
      addItem: action,
      removeItem: action,
      fromJSON: action,
      setName: action,
      reorderItems: action,
      disposeSaveHandler: false,
      setDeleted: action,
      setLastError: action,
      setSaving: action,
      reorderItemsLocally: action,
    })
    this.store = store
    this.id = uuidv4()
    this.name = name
    this.timestamp = Date.now()
    // this.enableSaveHandler()
  }

  get sortedItems() {
    return this.items
      .slice()
      .sort((a: TemplateItem, b: TemplateItem) => a.position - b.position)
  }

  get sortedItemsWithoutSection() {
    return this.sortedItems.filter((item) => !item.sectionId)
  }

  get itemLookup() {
    const lookup: Record<string, TemplateItem> = {}
    this.items.forEach((item) => (lookup[item.id] = item))
    return lookup
  }

  get sectionLookup() {
    const lookup: Record<string, TemplateSection> = {}
    this.sections.forEach((section) => (lookup[section.id] = section))
    return lookup
  }

  getItem(id: string): TemplateItem | undefined {
    return this.itemLookup[id] || undefined
  }

  getSection(id: string): TemplateSection | undefined {
    return this.sectionLookup[id] || undefined
  }

  // enableSaveHandler = () => {
  //   // remove the old one
  //   this.disposeSaveHandler?.()
  //   this.disposeSaveHandler = reaction(
  //     () => this.asJSON(),
  //     () => this.handleUpdate()
  //   )
  // }

  // disableSaveHandler = () => {
  //   this.disposeSaveHandler?.()
  //   this.disposeSaveHandler = undefined
  // }

  setDeleted(value: boolean) {
    this.deleted = value
  }

  setSaving(value: boolean) {
    this.saving = value
  }

  setLastError(value: string | undefined) {
    this.lastError = value || ''
  }

  get hasError() {
    return Boolean(this.lastError)
  }

  async handleCreate() {
    this.setSaving(true)
    this.setLastError(undefined)
    try {
      const template = await this.store.provider.createTemplate({
        id: this.id,
        name: this.name,
        deadline: this.deadline,
      })
      return this.fromJSON(template)
    } catch (e) {
      this.setLastError('Failed to create')
      throw e
    } finally {
      this.setSaving(false)
    }
  }

  update = (data: Partial<Template>) => {
    this.fromJSON(data)
    this.handleUpdate()
  }

  handleUpdate = async () => {
    this.setSaving(true)
    this.setLastError(undefined)
    const payload = {
      name: this.name,
      deadline: this.deadline,
    }
    this.store.provider
      .updateTemplate(this.id, payload)
      .then((template) => {
        this.fromJSON({ name: template.name })
      })
      .catch((e) => {
        this.setLastError('Failed to update')
        throw e
      })
      .finally(() => {
        this.setSaving(false)
      })
  }

  handleDelete = async () => {
    this.setLastError(undefined)
    this.setDeleted(true)
    return this.store.provider.deleteTemplate(this.id).catch((e) => {
      console.log('Failed to delete', e)
      // undelete
      this.setDeleted(false)
      this.setLastError('Failed to delete')
      throw e
    })
  }

  buildItem = (name: string) => {
    return new TemplateItem(this.store, this, name)
  }

  addItem = async (name: string) => {
    console.log('Adding item', name)
    const item = this.buildItem(name)
    this.items.push(item)
    await item.handleCreate()
  }

  removeItem(id: string) {
    const found = this.getItem(id)
    if (!found) return
    this.items.remove(found)
    found.handleDelete()
  }

  removeSection(id: string) {
    const found = this.getSection(id)
    if (!found) return
    this.sections = this.sections.filter((s) => s.id !== id)
    found.handleDelete()
  }

  addSection = (name: string) => {
    const section = new TemplateSection(this.store, this, name)
    this.sections.push(section)
    section.handleCreate()
  }

  setName = (name: string) => {
    this.name = name
    this.handleUpdate()
  }

  delete = () => {
    return this.store.provider.deleteTemplate(this.id)
  }

  asJSON() {
    return {
      id: this.id,
      name: this.name,
      items: this.items.map((item) => item.asJSON()),
      timestamp: this.timestamp,
    }
  }

  touch() {
    this.timestamp = Date.now()
  }

  reorderItemsLocally(newIds: string[]) {
    const lookup = new Map(this.items.map((item) => [item.id, item]))
    for (let index = 0; index < newIds.length; index++) {
      const key = newIds[index]
      if (!key) continue
      const item = lookup.get(key)
      if (item) {
        item.position = index + 1
      }
    }
  }

  async reorderItems(newIds: Record<string, string[]>) {
    // const originalIds = this.items.map((item) => item.id)

    // if (originalIds.join(":") === newIds.join(":")) return

    // // optimistic
    // this.reorderItemsLocally(newIds)

    // await sleep(1000)
    // backend
    try {
      // const result =
      await this.store.provider.reorderTemplateItems(this.id, newIds)
      // if (result) {
      //   // commit
      //   this.reorderItemsLocally(result.items.map((item) => item.id))
      // } else {
      //   // revert
      //   this.reorderItemsLocally(originalIds)
      // }
    } catch {
      // revert
      // this.reorderItemsLocally(originalIds)
    }
  }

  fromJSON(json: string | object) {
    const { id, name, items, sections, timestamp, deadline } =
      typeof json === 'object' ? json : JSON.parse(json)
    if (id) this.id = id
    if (name) this.name = name
    if (timestamp) this.timestamp = timestamp
    if (deadline || deadline === null) this.deadline = deadline
    if (items) {
      this.items = items.map((data: TemplateItem) => {
        const item = new TemplateItem(this.store, this, data.name)
        item.fromJSON(data)
        return item
      })
    }
    if (sections) {
      this.sections = sections.map((data: ProviderTemplateSection) => {
        const item = new TemplateSection(this.store, this, data.name)
        item.fromProvider(data)
        return item
      })
    }
  }

  dispose() {
    // this.disableSaveHandler()
  }
}
